import PropTypes from 'prop-types';
import React, { createRef, useEffect, useState, useMemo, createContext, useContext } from 'react';

import { loadScript } from '$utilities/requests';

/* ========================================================================= */
/* Styles */
/* ========================================================================= */

const mapStyles = [
    {
        'featureType': 'administrative',
        'elementType': 'labels.text.fill',
        'stylers': [
            {
                'color': '#444444'
            }
        ]
    },
    {
        'featureType': 'administrative.province',
        'elementType': 'labels',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'administrative.locality',
        'elementType': 'labels',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'administrative.neighborhood',
        'elementType': 'labels',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'landscape',
        'elementType': 'all',
        'stylers': [
            {
                'color': '#ededed'
            }
        ]
    },
    {
        'featureType': 'poi',
        'elementType': 'all',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'road',
        'elementType': 'all',
        'stylers': [
            {
                'saturation': -100
            },
            {
                'lightness': 45
            }
        ]
    },
    {
        'featureType': 'road.highway',
        'stylers': [
            {
                'visibility': 'simplified'
            }
        ]
    },
    {
        'featureType': 'road',
        'elementType': 'labels',
        'stylers': [
            {
                'visibility': 'simplified'
            }
        ]
    },
    {
        'featureType': 'road.highway',
        'elementType': 'labels',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'road.arterial',
        'elementType': 'labels.icon',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'road.local',
        'elementType': 'labels',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'transit',
        'elementType': 'geometry.fill',
        'stylers': [
            {
                'saturation': -100
            },
            {
                'lightness': 45
            }
        ]
    },
    {
        'featureType': 'transit',
        'elementType': 'geometry.stroke',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'transit',
        'elementType': 'labels.icon',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'transit',
        'elementType': 'labels.text',
        'stylers': [
            {
                'visibility': 'off'
            }
        ]
    },
    {
        'featureType': 'water',
        'elementType': 'all',
        'stylers': [
            {
                'color': '#c7c6c7'
            },
            {
                'visibility': 'on'
            }
        ]
    }
];

/* ========================================================================= */
/* Shared variables */
/* ========================================================================= */

const sharedMemory = {
    isRequested: false,
    isLoaded: false,
    queue: []
};

/* ========================================================================= */
/* Hooks and functions */
/* ========================================================================= */

const Context = createContext();

/**
 * @function createMap
 * @param {object} mapRef - The reference to the map container.
 * @param {object}
 *     @prop {string} lat - The latitude on which center the map.
 *     @prop {string} lng - The longitude on which center the map.
 * @return {object} - The map instance.
 *
 * Creates a Google Map instance with the provided params.
 */
const createMap = (mapRef, { lat, lng }) => {
    return new window.google.maps.Map(
        mapRef.current,
        {
            center: { lat, lng },
            fullscreenControl: false,
            mapTypeControl: false,
            mapTypeId: window.google.maps.MapTypeId.ROADMAP,
            maxZoom: 18,
            minZoom: 0,
            scaleControl: false,
            scrollwheel: false,
            scrollWheelZoom:'center',
            streetViewControl: false,
            zoom: 9,
            zoomControl: false,
            styles: mapStyles
        }
    );
};

/**
 * @function createMarker
 * @param {object} googleMapInstance - The Google Map instance.
 * @param {object}
 *     @prop {string} lat - The latitude on which center the map.
 *     @prop {string} lng - The longitude on which center the map.
 * @return {object} - The marker instance.
 *
 * Creates a marker instance with the provided params.
 */
const createMarker = (googleMapInstance, { lat, lng }) => {
    return new window.google.maps.Marker({
        map: googleMapInstance,
        position: { lat, lng },
        title: '',
        icon: {
            url: window.location.origin + '/images/google-maps-marker.svg',
            size: new window.google.maps.Size(23, 34),
            scaledSize: new window.google.maps.Size(23, 34),
        }
    });
};

/**
 * @hook useGoogleMap
 * @param {object} mapRef - The reference to the map container
 * @param {object} mapOptions
 *     @prop {string} lat
 *     @prop {string} lng
 *     @prop {string} key - The API key provided by Google.
 * @return {object|null} - The Google map instance
 *
 * 1. Initialises Google Maps when the component is mounted in the browser.
 * 1.1. If the Google Maps scripts are not loaded yet, let’s load it and execute the queue once initialised.
 * 1.2. Otherwise, let’s initialise the Google Map instance on load.
 */
const useGoogleMap = (mapRef, mapOptions) => {
    const [ googleMap, setGoogleMap ] = useState(null);

    const init = () => {
        const googleMapInstance = createMap(mapRef, mapOptions);
        createMarker(googleMapInstance, mapOptions);
        setGoogleMap(googleMapInstance);
    };

    /* [1] */
    useEffect(() => {
        if (!sharedMemory.isRequested && !window.google?.maps && mapOptions.key) {
            sharedMemory.isRequested = true;
            sharedMemory.queue.push(init);

            /* [1.1] */
            loadScript('https://maps.googleapis.com/maps/api/js?key=' + mapOptions.key, () => {
                sharedMemory.isLoaded = true;
                sharedMemory.queue.forEach(callback => callback());
            });
        } else if (!googleMap && mapRef.current) {
            /* [1.2] */
            sharedMemory.isLoaded ? init() : sharedMemory.queue.push(init);
        }
    }, []);

    return googleMap;
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @component Map
 * @props (see propTypes)
 *
 * 1. Initialises Google Maps.
 */
const Map = ({ lat, lng, ...remainingProps }) => {
    const mapRef = createRef(null);
    const [ mapType, setMapType ] = useState('roadmap');

    /* [1] */
    const googleMap = useGoogleMap(mapRef, {
        lat: parseFloat(lat),
        lng: parseFloat(lng),
        key: 'AIzaSyAXEe8jyKBX7_fl0bG7tszoY47idXwW_7c'
    });

    const context = useMemo(() => ({ googleMap, mapType, setMapType, mapRef }), [ googleMap, mapType ]);

    return (
        <Context.Provider value={context}>
            <div {...remainingProps} />
        </Context.Provider>
    );
};

const MapCanvas = props => {
    const { mapRef } = useContext(Context);

    return (
        <div
            ref={mapRef}
            {...props} />
    );
};

const MapTypeButton = ({ type, ...remainingProps }) => {
    const { mapType, setMapType } = useContext(Context);

    return (
        <button
            aria-disabled={mapType === type}
            onClick={() => setMapType(type)}
            type='button'
            {...remainingProps} />
    );
};


/* ========================================================================= */
/* Typing & exports */
/* ========================================================================= */

Map.propTypes = {
    lat: PropTypes.string.isRequired,
    lng: PropTypes.string.isRequired,
};

MapCanvas.propTypes = {};

MapTypeButton.propTypes = {
    type: PropTypes.string,
};

export {
    Map,
    MapCanvas,
    MapTypeButton,
};
